import {
  RA,
  TOOLTIP_DELAY_CLOSE,
  TOOLTIP_DELAY_OPEN,
  TOOLTIP_OFFSET_IN_PX,
} from '@make.tv/lib-web-uicomponents';
import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Label } from 'semantic-ui-react';

import { STATUS_LEGEND } from 'Constants';
import { Typography } from 'Components/typography';

const Divider = styled.hr`
  border: 0.5px solid #555;
  display: block;
  margin-top: 0.5em;
  margin-bottom: 0.5em;
  margin-left: auto;
  margin-right: auto;
`;

const LegendTooltipContent = styled(RA.Tooltip)`
  padding: 15px;
  border-radius: 6px;
  text-align: left;
  background-color: rgb(27, 28, 29);
  width: max-content;
  outline: thin solid #555;
  pointer-events: none;
  user-select: none;
`;

const LegendStatusTrigger = styled.div`
  cursor: default;
`;

function LegendTooltip({
  children,
  content,
  placement = 'bottom start',
}) {
  return (
    <RA.TooltipTrigger closeDelay={TOOLTIP_DELAY_CLOSE} delay={TOOLTIP_DELAY_OPEN}>
      <RA.Focusable>
        {children}
      </RA.Focusable>
      <LegendTooltipContent offset={TOOLTIP_OFFSET_IN_PX} placement={placement}>
        {content}
      </LegendTooltipContent>
    </RA.TooltipTrigger>
  );
}

const BulletList = styled.ul`
  margin-top: 0;
  padding-top: 0;
  margin-bottom: 5px;
  list-style: disc;
  padding-inline-start: 40px;
`;

const MonitoringStatusItemWithDetailsStyle = createGlobalStyle`
  .mon-status-item-container {
    position: static;
    height: min-content;
    width: min-content;

    .mon-details {
      visibility: hidden;
      position: absolute;
      z-index: 1;
      padding: 5px 8px;
      margin-top: 4px;
      border-radius: 6px;
      background-color: rgb(27, 28, 29);
      width: max-content;
    }
  }

  .mon-status-item-container:hover .mon-details {
    visibility: visible;
  }
`;

const StyledMark = styled.div.attrs((props) => ({
  ...props,
}))`
    background-color: ${({ bgcolor, themecolormap = 'monitoringStatus', theme }) => theme.colors[themecolormap][bgcolor]};
    color: black;
    border-radius: 3px;
    padding: 2px 5px;
    font-weight: bold;
    display: inline;
`;

export const MonitoringStatusItem = styled(Label).attrs((props) => ({
  circular: true,
  empty: true,
  size: 'big',
  ...props,
}))`
  &.ui.label {
    background: ${({
    bgcolor, secondarybgcolor, colorMap, themecolormap = colorMap || 'monitoringStatus', theme
  }) => {
    if (secondarybgcolor) {
      const top = theme.colors[themecolormap][bgcolor];
      const bottom = theme.colors[themecolormap][secondarybgcolor];
      return `linear-gradient( -45deg, ${bottom}, ${bottom} 50%, ${top} 50% )`;
    }
    return theme.colors[themecolormap][bgcolor];
  }}!important;
    border-color: #000!important;
    padding: 0.38em!important;
    border: 2px solid;
  }
`;

export const MonitoringStatusItemWithDetails = ({ bgcolor, details }) => (
  <>
    <MonitoringStatusItemWithDetailsStyle />
    <div className="mon-status-item-container">
      <MonitoringStatusItem bgcolor={bgcolor} />
      {details && (
        <div className="mon-details">{details}</div>
      )}
    </div>
  </>
);

export const MonitoringStatusLegend = () => STATUS_LEGEND.map((item) => (
  <LegendTooltip
    key={item.key}
    content={(
      <>
        {item.key === 'red' && (
          <>
            <StyledMark bgcolor="red">Critical</StyledMark>
            <br />
            <br />
            Operational impact or severe failure
            <br />
            <br />
            <b>Examples:</b>
            <br />
            Service impacting issue
            <BulletList>
              <li>Site offline</li>
              <li>Critical service down</li>
            </BulletList>
            <Divider />
            <b>Action required:</b>
            {' '}
            Immediate response required
            <br />
          </>
        )}
        {item.key === 'yellow' && (
          <>
            <StyledMark bgcolor="yellow">Warning</StyledMark>
            <br />
            <br />
            Needs attention, degraded performance
            <br />
            <br />
            <b>Examples:</b>
            <br />
            Non-service impacting issue
            <BulletList>
              <li>Not in production</li>
              <li>Unknown status</li>
            </BulletList>
            <Divider />
            <b>Action required:</b>
            {' '}
            Monitor and investigate
            <br />
          </>
        )}
        {item.key === 'green' && (
          <>
            <StyledMark bgcolor="green">Operational</StyledMark>
            <br />
            <br />
            Fully functioning and operational
            <br />
            <br />
            <b>Examples:</b>
            <br />
            Connected with video
            <BulletList>
              <li>In production</li>
              <li>Services running</li>
            </BulletList>
            <Divider />
            <b>Action required:</b>
            {' '}
            No action needed
            <br />
          </>
        )}
        {item.key === 'purple' && (
          <>
            <StyledMark bgcolor="purple">Informational</StyledMark>
            <br />
            <br />
            Active but not production-critical
            <br />
            <br />
            <b>Examples:</b>
            <br />
            Connected without video
            <BulletList>
              <li>Installing</li>
              <li>Diagnostic mode</li>
            </BulletList>
            <Divider />
            <b>Action required:</b>
            {' '}
            Awareness only
            <br />
          </>
        )}
        {item.key === 'gray' && (
          <>
            <StyledMark bgcolor="gray">Inactive</StyledMark>
            <br />
            <br />
            Intentionally not operational
            <br />
            <br />
            <b>Examples:</b>
            <br />
            Testing
            <BulletList>
              <li>Inactive</li>
              <li>Not running (planned)</li>
            </BulletList>
            <Divider />
            <b>Action required:</b>
            {' '}
            Expected state
            <br />
          </>
        )}
      </>
    )}
    placement="bottom start"
  >
    <LegendStatusTrigger className="d-flex items-center mx-1" role="button">
      <MonitoringStatusItem circular bgcolor={item.key} empty size="medium" />
      <Typography variant="caption" className="ml-1">{item.label}</Typography>
    </LegendStatusTrigger>
  </LegendTooltip>
));
